import { ShieldCheck, Medal } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ShineBorder } from "@/components/ui/shine-border";
import { GlowCard } from "@/components/ui/spotlight-card";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";
import { beginCheckout } from "@/lib/analytics";
import { FEATURES, FEATURES_2 } from "./PricingSection.data";
import { Marquee } from "./pricing/Marquee";
import { TestimonialFeatureSectionFour } from "./TestimonialFeatureSectionFour";

type TrustCard = {
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  description: string;
};

const TRUST_CARDS: TrustCard[] = [
  {
    Icon: Medal,
    title: "Senior-Level Work",
    description: "A Designer with 20 years of brand and web experience.",
  },
  {
    Icon: ShieldCheck,
    title: "100% Money Back",
    description: "Not happy after round one? Full refund, no questions.",
  },
];

function TrustCardItem({ Icon, title, description }: TrustCard) {
  return (
    <div className="flex items-center gap-4 rounded-[24px] border border-foreground/10 bg-card/60 px-4 py-3">
      <div className="flex shrink-0 items-center justify-center">
        <Icon className="h-10 w-10 text-brand-blue" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <h4 className="text-[0.95rem] font-bold leading-tight text-foreground">{title}</h4>
        <p className="mt-1 text-[0.8rem] leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
    </div>
  );
}

interface SimplePricingSectionProps {
  onCtaClick?: () => void;
}

export function SimplePricingSection({ }: SimplePricingSectionProps) {
  return (
    <section id="pricing" className="relative isolate overflow-hidden bg-background pt-6 pb-0 sm:pt-8">
      <div className="mx-auto max-w-7xl px-6">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
      </div>
      <div className="mx-auto max-w-7xl px-6">
        <div className="mt-12 sm:mt-16 grid grid-cols-1 items-stretch gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
          {/* Left: Sharelo video + trust cards */}
          <div className="order-2 flex flex-col gap-6 lg:order-1">
            <div className="rounded-[1.875rem] bg-gradient-blue-pink-soft p-1 shadow-card sm:p-1.5">
              <ShareloEmbed
                id="dyr6DN2BEKowGy3DQkmeW"
                title="Risk reversal — customer story"
                className="w-full"
                preload
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {TRUST_CARDS.map((card) => (
                <TrustCardItem key={card.title} {...card} />
              ))}
            </div>
          </div>

          {/* Right: pricing copy */}
          <div className="order-1 flex flex-col justify-center text-center lg:order-2">
            <div>
              <h3 className="font-bold leading-[1.05] tracking-tight text-[2.25rem] sm:text-5xl lg:text-5xl text-foreground">
                What a website <span className="text-gradient-cycle">costs with us</span>
              </h3>
              <p className="mt-3 text-base text-muted-foreground">
                One scope, one quote, and you see a
                <br className="sm:hidden" />
                mockup before you pay anything.
              </p>
            </div>

            {/* Starting price card — shine border + gradient */}
            <div id="pricing-web-design" className="scroll-mt-24 mt-10">
              <ShineBorder
                borderRadius={16}
                borderWidth={4}
                duration={6}
                color={["#3b82f6", "#a855f7", "#ec4899"]}
                className="relative"
              >
                <GlowCard
                  glowColor="blue"
                  disableInnerGlow
                  customSize
                  className="w-full p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 bg-card"
                >
                  {/* Left: starting price */}
                  <div className="flex flex-col items-center text-center">
                    <p className="text-sm font-medium text-muted-foreground">Projects start at</p>
                    <p className="mt-2 text-5xl sm:text-6xl font-bold leading-none bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">
                      $1,995
                    </p>
                  </div>

                  {/* Thin separator */}
                  <div className="hidden sm:block h-24 w-px bg-foreground/15" />
                  <div className="block sm:hidden h-px w-full bg-foreground/15" />

                  {/* Right: hosting info */}
                  <div className="flex flex-col items-center text-center">
                    <p className="flex items-baseline gap-1 text-4xl sm:text-5xl font-bold text-foreground">
                      <span className="text-foreground/60">+</span>
                      <span className="text-foreground/60">$99</span>
                      <span className="text-lg sm:text-xl font-medium text-muted-foreground">/mo</span>
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">Hosting, updates, and support</p>
                  </div>
                </GlowCard>
              </ShineBorder>
            </div>

            <p className="mt-8 text-center text-sm leading-relaxed text-muted-foreground">
              Cancel anytime. You own the site outright, and hosting transfers with you if you leave.
            </p>

            <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:gap-4">
              <Button
                asChild
                className="h-12 rounded-full px-8 font-semibold"
              >
                <Link to="/get-mockup" search={{ plan: "launch" }} onClick={() => beginCheckout("launch")}>
                  Get a Free Mockup
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 rounded-full px-8 font-semibold"
              >
                <a href="#contact">Book an intro call</a>
              </Button>
            </div>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              No call required. Send your details and we build one.
            </p>
          </div>
        </div>
      </div>

      {/* Pro pills */}
      <div className="mx-auto max-w-6xl px-6">
        <div className="mt-10 sm:mt-12">
          <Marquee items={FEATURES} variant="blue" />
        </div>
        <div className="mt-4 mb-0">
          <Marquee items={FEATURES_2} variant="pink" reverse />
        </div>
      </div>

      <TestimonialFeatureSectionFour />
    </section>
  );
}
